import { supabase } from "../../lib/supabase";

/* ---------------------------------------------------------------- types */

export interface PersonListRow {
  id: number;
  full_name: string;
  email: string | null;
  job_title: string | null;
  engineer_type: string | null;
  is_contractor: boolean;
  roster_active: boolean;
  legacy_glide_id: number | null;
  legacy_wip_people_id: number | null;
  profile_id: string | null;
  auth_linked: boolean;
  auth_email: string | null;
  last_sign_in_at: string | null;
  is_active: boolean | null;
  approved: boolean;
  primary_role_key: string | null;
  primary_role_name: string | null;
  intended_role_key: string | null;
}

export interface PersonRecord {
  id: number;
  legacy_glide_id: number | null;
  legacy_wip_people_id: number | null;
  full_name: string;
  email: string | null;
  job_title: string | null;
  engineer_type: string | null;
  is_contractor: boolean;
  active: boolean;
  profile_id: string | null;
  intended_role_id: number | null;
  intended_role_key: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  auth_email: string | null;
  last_sign_in_at: string | null;
  is_active: boolean | null;
}

export interface RoleAssignment {
  role_key: string;
  role_name: string;
  is_primary: boolean;
  granted_by: string | null;
  granted_at: string;
}

export interface PermissionOverride {
  permission_key: string;
  granted: boolean;
  note: string | null;
  granted_by: string | null;
  granted_at: string;
}

export interface AuditEntry {
  change_type: string;
  role_key: string | null;
  permission_key: string | null;
  old_value: string | null;
  new_value: string | null;
  note: string | null;
  actor: string | null;
  created_at: string;
}

export interface PersonDetail {
  person: PersonRecord;
  roles: RoleAssignment[];
  effective_permissions: string[];
  overrides: PermissionOverride[];
  audit: AuditEntry[];
}

export interface RoleOption {
  id: number;
  key: string;
  name: string;
}

export interface PermissionOption {
  id: number;
  key: string;
  name: string;
}

/* ---------------------------------------------------------------- reads */

/**
 * Someone who has signed in but has never been let in.
 *
 * Signing in with a Viewpoint Microsoft account grants nothing by design
 * (see migration 0026), so this is the queue of people locked out until an
 * administrator approves them. my_pending_approvals() decides who may see
 * it -- callers without can_manage_people get an empty list, not an error.
 */
export interface PendingApproval {
  profile_id: string;
  /** Null when the sign-in has no staff roster row -- there is no People page to link to. */
  person_id: number | null;
  full_name: string;
  email: string | null;
  requested_at: string;
  last_sign_in_at: string | null;
  on_roster: boolean;
}

export async function fetchPendingApprovals(): Promise<PendingApproval[]> {
  const { data, error } = await supabase.rpc("my_pending_approvals");
  if (error) throw error;
  return (data ?? []) as PendingApproval[];
}

export async function fetchPeople(): Promise<PersonListRow[]> {
  const { data, error } = await supabase.rpc("people_list");
  if (error) throw error;
  return (data as PersonListRow[] | null) ?? [];
}

export async function fetchPersonDetail(personId: number): Promise<PersonDetail> {
  const { data, error } = await supabase.rpc("people_detail", { p_person_id: personId });
  if (error) throw error;
  return data as PersonDetail;
}

export async function fetchRoleOptions(): Promise<RoleOption[]> {
  const { data, error } = await supabase.from("roles").select("id,key,name").order("name");
  if (error) throw error;
  return (data ?? []) as RoleOption[];
}

export async function fetchPermissionOptions(): Promise<PermissionOption[]> {
  const { data, error } = await supabase.from("permissions").select("id,key,name").order("name");
  if (error) throw error;
  return (data ?? []) as PermissionOption[];
}

/* --------------------------------------------------------------- writes */

export async function approvePerson(personId: number, roleKey: string): Promise<PersonDetail> {
  const { data, error } = await supabase.rpc("people_approve", {
    p_person_id: personId,
    p_role_key: roleKey,
  });
  if (error) throw error;
  return data as PersonDetail;
}

export async function setPrimaryRole(profileId: string, roleKey: string): Promise<void> {
  const { error } = await supabase.rpc("people_set_primary_role", {
    p_profile_id: profileId,
    p_role_key: roleKey,
  });
  if (error) throw error;
}

export async function addRole(profileId: string, roleKey: string): Promise<void> {
  const { error } = await supabase.rpc("people_add_role", {
    p_profile_id: profileId,
    p_role_key: roleKey,
  });
  if (error) throw error;
}

export async function removeRole(profileId: string, roleKey: string): Promise<void> {
  const { error } = await supabase.rpc("people_remove_role", {
    p_profile_id: profileId,
    p_role_key: roleKey,
  });
  if (error) throw error;
}

export async function deactivatePerson(profileId: string, reason: string | null): Promise<void> {
  const { error } = await supabase.rpc("people_deactivate", {
    p_profile_id: profileId,
    p_reason: reason,
  });
  if (error) throw error;
}

export async function reactivatePerson(profileId: string, roleKey: string | null): Promise<void> {
  const { error } = await supabase.rpc("people_reactivate", {
    p_profile_id: profileId,
    p_role_key: roleKey,
  });
  if (error) throw error;
}

export async function addOverride(
  profileId: string,
  permissionKey: string,
  granted: boolean,
  note: string | null,
): Promise<void> {
  const { error } = await supabase.rpc("people_add_override", {
    p_profile_id: profileId,
    p_permission_key: permissionKey,
    p_granted: granted,
    p_note: note,
  });
  if (error) throw error;
}

export async function removeOverride(profileId: string, permissionKey: string): Promise<void> {
  const { error } = await supabase.rpc("people_remove_override", {
    p_profile_id: profileId,
    p_permission_key: permissionKey,
  });
  if (error) throw error;
}

/* ------------------------------------------------------------ compliance */

export type CertStatus = "no_expiry" | "expired" | "expiring_soon" | "valid";

export interface Certification {
  id: number;
  profile_id: string;
  full_name: string;
  person_id: number | null;
  cert_type: string;
  reference: string | null;
  issued_on: string | null;
  expires_on: string | null;
  file_id: number | null;
  notes: string | null;
  status: CertStatus;
}

/** certifications' own RLS already scopes this to "your own rows, or
 * can_manage_people/can_view_compliance" — no profile_id filter needed
 * here for the per-person tab; the global Compliance page relies on the
 * same RLS to see everyone. */
export async function fetchCertifications(profileId?: string): Promise<Certification[]> {
  let q = supabase.from("v_certifications_list").select("*").order("expires_on", { ascending: true, nullsFirst: false });
  if (profileId) q = q.eq("profile_id", profileId);
  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as Certification[];
}

export interface CertificationInput {
  profile_id: string;
  cert_type: string;
  reference: string | null;
  issued_on: string | null;
  expires_on: string | null;
  notes: string | null;
}

export async function createCertification(input: CertificationInput): Promise<void> {
  const { error } = await supabase.from("certifications").insert(input);
  if (error) throw error;
}

export async function updateCertification(id: number, input: Partial<CertificationInput>): Promise<void> {
  const { error } = await supabase.from("certifications").update(input).eq("id", id);
  if (error) throw error;
}

export async function deleteCertification(id: number): Promise<void> {
  const { error } = await supabase.from("certifications").delete().eq("id", id);
  if (error) throw error;
}

/* ------------------------------------------- DVLA driving licence checks */

/**
 * Query-time due state from migration 0372: nothing here decides when a
 * re-check is due, the database does. "never" is a person with no check
 * at all -- shown, not hidden, because that is what enforcement means.
 */
export type LicenceDueState = "never" | "pending" | "rejected" | "overdue" | "due_soon" | "ok";

export interface MyLicenceStatus {
  id?: number;
  status?: "pending" | "approved" | "rejected";
  submitted_at?: string | null;
  reviewed_at?: string | null;
  next_due_date?: string | null;
  due_state: LicenceDueState;
}

export async function fetchMyLicenceStatus(): Promise<MyLicenceStatus> {
  const { data, error } = await supabase.rpc("my_licence_status");
  if (error) throw error;
  return data as MyLicenceStatus;
}

export async function submitLicenceCheck(code: string): Promise<void> {
  const { error } = await supabase.rpc("licence_check_submit", { p_code: code });
  if (error) throw error;
}

export interface LicenceQueueItem {
  id: number;
  driver_id: string;
  full_name: string;
  check_code: string;
  submitted_at: string;
}

export async function fetchLicenceQueue(): Promise<LicenceQueueItem[]> {
  const { data, error } = await supabase.rpc("licence_review_queue");
  if (error) throw error;
  return (data ?? []) as LicenceQueueItem[];
}

export async function reviewLicenceCheck(id: number, approve: boolean, notes?: string): Promise<void> {
  const { error } = await supabase.rpc("licence_check_review", {
    p_id: id,
    p_approve: approve,
    p_notes: notes ?? null,
  });
  if (error) throw error;
}

export interface PersonLicenceStatus {
  profile_id: string;
  full_name: string;
  status: "pending" | "approved" | "rejected" | null;
  submitted_at: string | null;
  reviewed_at: string | null;
  next_due_date: string | null;
  due_state: LicenceDueState;
}

export async function fetchPeopleLicenceStatuses(): Promise<PersonLicenceStatus[]> {
  const { data, error } = await supabase.rpc("people_licence_statuses");
  if (error) throw error;
  return (data ?? []) as PersonLicenceStatus[];
}
